import type { CallApi } from "@/hooks/apiHooks/useApi";

// Browser-side half of an authenticated file download (E12-S5 / #240): the
// bytes arrive through useApi as a Blob (the endpoint needs the bearer token,
// so a plain <a href> to it cannot work), and the name comes from the
// response's Content-Disposition header. One pipeline for every download —
// invoices, location certificates, organization files (#428).

// Both forms are anchored to the start of a parameter (`^` or `;`) so a
// parameter that merely ends in "filename" (xfilename=, orig-filename=) is not
// read as the name. RFC 5987's value is `charset'[language]'value` — the
// language tag is optional but legal (`UTF-8'en'…`), so it is allowed empty
// or not.
const EXTENDED_FORM = /(?:^|;)\s*filename\*\s*=\s*(?:[\w-]+)'[\w-]*'([^;]+)/i;
const PLAIN_FORM = /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]+))/i;

// A name that would leave the download folder — a path separator or a bare
// `..` segment. Browsers sanitise `download` today; the next caller may not go
// through an <a download>.
const isPathLike = (name: string): boolean =>
    /[/\\]/.test(name) || name === "..";

// Reads the file name out of a Content-Disposition header. ASP.NET's
// FileResult writes both forms — `filename="cert.pdf"; filename*=UTF-8''cert.pdf`
// — and the RFC 5987 `filename*` is the authoritative one when present
// (it survives non-ASCII names). Returns undefined when the header is absent,
// names no file, or names a path, so the caller can fall back to a name it
// already knows.
export const fileNameFromContentDisposition = (
    header: string | null,
): string | undefined => {
    if (!header) {
        return undefined;
    }

    let name: string | undefined;

    const extended = EXTENDED_FORM.exec(header);
    if (extended) {
        try {
            name = decodeURIComponent(extended[1].trim()) || undefined;
        } catch {
            // Malformed percent-encoding — fall through to the plain form.
        }
    }

    if (!name) {
        const plain = PLAIN_FORM.exec(header);
        name = (plain?.[1] ?? plain?.[2])?.trim();
    }

    return name && !isPathLike(name) ? name : undefined;
};

// A download failure the caller has no specific sentence for: a status the
// API did not author (a body-less or HTML 4xx from the Functions host or a
// gateway), a 5xx or a network failure (useApi has toasted those), a body
// that could not be read, or a 2xx that was not a file. Carries no `status`,
// so a caller's status mapping lands on its generic sentence.
export class DownloadError extends Error {
    constructor(
        message: string,
        public cause?: unknown,
    ) {
        super(message);
        this.name = "DownloadError";
    }
}

export interface DownloadedFile {
    blob: Blob;
    // The server's Content-Disposition name; undefined when it sent none or
    // the header was unreadable, and the caller uses a name it already has.
    fileName: string | undefined;
}

export interface FetchDownloadOptions {
    // How a failed request reaches the caller.
    // "contracted": useApi's toast stays, except for a 403 or 404 the API
    //   itself authored — those reject as the ApiError, for the caller's own
    //   inline sentence. Every other request failure, and a body that cannot
    //   be read, rejects as DownloadError.
    // "silent": no toast for any failure; the request's rejection, and the
    //   body read's, pass through untouched.
    failures: "contracted" | "silent";
    // The content types a 2xx may carry, matched as case-insensitive
    // prefixes. Omitted: any type but an HTML page.
    acceptedTypes?: readonly string[];
}

// A gateway's sign-in or "session expired" page answers 200 too. Saved under
// the file's name it would read as success. MIME types are case-insensitive.
const PAGE_TYPES = ["text/html", "application/xhtml+xml"];

const isFile = (
    blob: Blob,
    contentType: string,
    acceptedTypes: readonly string[] | undefined,
): boolean => {
    const type = contentType.toLowerCase();

    if (blob.size === 0 || PAGE_TYPES.some((page) => type.startsWith(page))) {
        return false;
    }

    return (
        !acceptedTypes ||
        acceptedTypes.some((accepted) => type.startsWith(accepted.toLowerCase()))
    );
};

const requestDownload = async (
    callApi: CallApi,
    path: string,
    failures: FetchDownloadOptions["failures"],
): Promise<Response> => {
    if (failures === "silent") {
        return callApi(path, {
            responseType: "response",
            skipErrorNotification: true,
        });
    }

    // Set by the predicate as useApi judges the failure, read in the catch:
    // the predicate is the one place that sees whether the API authored the
    // body. A body-less or HTML 404 is the host or a gateway, not "removed".
    let isContracted = false;

    try {
        return await callApi(path, {
            responseType: "response",
            skipErrorNotification: (status, _errorCode, hasApiBody) => {
                isContracted =
                    (status === 403 || status === 404) && hasApiBody;

                return isContracted;
            },
        });
    } catch (caught) {
        if (isContracted) {
            throw caught;
        }

        throw new DownloadError(`The request for ${path} failed.`, caught);
    }
};

// GETs `path` through useApi (bearer token) and returns the bytes plus the
// server's name for them. Rejects per `failures`, and always with
// DownloadError for a 2xx that is not a file.
export const fetchDownload = async (
    callApi: CallApi,
    path: string,
    { failures, acceptedTypes }: FetchDownloadOptions,
): Promise<DownloadedFile> => {
    const response = await requestDownload(callApi, path, failures);

    // Past callApi's own try/catch: a body that fails to read (the
    // connection dropped mid-file) rejects with a bare TypeError.
    let blob: Blob;

    try {
        blob = await response.blob();
    } catch (caught) {
        if (failures === "silent") {
            throw caught;
        }

        throw new DownloadError(`The body of ${path} could not be read.`, caught);
    }

    const contentType = response.headers.get("content-type") ?? "";

    if (!isFile(blob, contentType, acceptedTypes)) {
        throw new DownloadError(
            `The response for ${path} was not a file (${contentType || "no content type"}, ${blob.size} bytes).`,
        );
    }

    return {
        blob,
        fileName: fileNameFromContentDisposition(
            response.headers.get("content-disposition"),
        ),
    };
};

// Hands a Blob to the browser as a download named `fileName`. The object URL
// is revoked on a later task, not in the click's own: Firefox and Safari
// start the download after the click event returns, and a URL revoked in the
// same task can leave them nothing to fetch.
export const saveBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
};
